import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  TreePine, ShieldCheck, Truck, Flame, Scissors, 
  Wrench, ArrowRight, Phone, Mail, CheckCircle, 
  Clock, Award, Hammer, Sparkles
} from 'lucide-react';
import { COMPANY_INFO, FALLBACK_PAGES, FALLBACK_GALLERIES } from '../data';
import GallerySection from '../components/GallerySection';

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    // Set Document Metadata
    document.title = "Skoventreprenør i Viborg, Himmerland & Vendsyssel - kontakt os nu";
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', "Skoventreprenør i Midt- og Nordjylland med naturpleje, maskinskovning og vognmand i hele landet. Kontakt os for et tilbud i dag");

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', "https://www.vesthimmerlands-maskinskovning.dk/");

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', "https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/1-forsidebillede.jpg");

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', "Skoventreprenør i Viborg, Himmerland & Vendsyssel - kontakt os nu");

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute('content', "Skoventreprenør i Midt- og Nordjylland med naturpleje, maskinskovning og vognmand i hele landet. Kontakt os for et tilbud i dag");

    // Fetch page content from public CMS endpoint
    fetch('https://api.dev.auxo.dk/public/content/pages')
      .then(res => res.json())
      .then(data => {
        const forside = data.find(p => p.page_slug === 'forside');
        if (forside) {
          setPageData(forside);
        }
      })
      .catch(err => {
        console.warn("Kunne ikke hente forside fra CMS, bruger fallback:", err);
        const fallback = FALLBACK_PAGES.find(p => p.page_slug === 'forside');
        setPageData(fallback);
      });

    // Fetch galleries for preview
    fetch('https://api.dev.auxo.dk/public/content/gallery')
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          const combined = [];
          data.forEach(g => {
            if (g.images && g.images.length > 0) {
              combined.push(g.images[0]);
              if (g.images.length > 1) {
                combined.push(g.images[1]);
              }
            }
          });
          setGalleryImages(combined.slice(0, 8)); // 8 images is a perfect grid size (2x4 or 4x2)
        } else {
          loadFallbackGallery();
        }
      })
      .catch(err => {
        console.warn("Kunne ikke hente galleri til forside, bruger fallback:", err);
        loadFallbackGallery();
      });

    function loadFallbackGallery() {
      const combined = [];
      Object.keys(FALLBACK_GALLERIES).forEach(key => {
        const imgs = FALLBACK_GALLERIES[key];
        if (imgs && imgs.length > 0) {
          combined.push(imgs[0]);
          if (imgs.length > 1) {
            combined.push(imgs[1]);
          }
        }
      });
      setGalleryImages(combined.slice(0, 8));
    }
  }, []);

  const categories = [
    {
      title: "Skovservice",
      description: "Professionel maskinskovning, effektiv vognmandskørsel samt flishugning med stor kapacitet.",
      icon: TreePine,
      href: "/skovservice",
      image: "https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/fa-ldeudka-rer-2.jpg",
      subservices: [
        { name: "Maskinskovning og udkørsel", href: "/skovservice/maskinskovning" },
        { name: "Vognmandskørsel", href: "/vognmand" },
        { name: "Flishugning", href: "/skovservice/flishugning" },
        { name: "Gravemaskine", href: "/skovservice/gravemaskine" }
      ]
    },
    {
      title: "Naturpleje",
      description: "Pleje af grønne områder, brakpudsning af græsarealer, grenknusning og hegnsklipning.",
      icon: Scissors,
      href: "/naturpleje",
      image: "https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/messenger-creation-ad909769-b7dd-4957-8045-222f0c1d3d66_1.jpeg",
      subservices: [
        { name: "Brakpudsning", href: "/naturpleje/brakpudsning" },
        { name: "Grenknusning", href: "/naturpleje/grenknusning" },
        { name: "Hegnsklipning", href: "/naturpleje/hegnsklipning" },
        { name: "Bomford Flailbot", href: "/naturpleje/bomford-flailbot" }
      ]
    },
    {
      title: "Stub- & Rodfræsning",
      description: "Effektiv fjernelse af træstubbe og trærødder ned til 40 cm dybde i hele landet.",
      icon: Wrench,
      href: "/stub-og-rodfraesning",
      image: "https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/1778ef6d-2b1c-4f48-bdb1-e2caa1af14b7.jpg",
      subservices: [
        { name: "Stubfræsning", href: "/stub-og-rodfraesning" },
        { name: "Rodfræsning", href: "/stub-og-rodfraesning" },
        { name: "Terrænklargøring" }
      ]
    }
  ];

  const stats = [
    { label: "Års Erfaring", value: "20+" },
    { label: "PEFC-godkendt", value: "Ja" },
    { label: "Professionelle Maskiner", value: "100%" },
    { label: "Geografisk dækning", value: "Hele DK" }
  ];

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-stone-950">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/1-forsidebillede.jpg" 
            alt="Moderne flishugger lastbil i dansk skov" 
            className="w-full h-full object-cover object-center opacity-40 scale-105 animate-pulse duration-10000"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/80 to-stone-900/40" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-swamp-950/90 border border-swamp-700 text-swamp-300 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
              <ShieldCheck className="w-4 h-4 text-swamp-300" />
              Godkendt til arbejde i PEFC-skove
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-none mb-6">
              Skoventreprenør i <br />
              <span className="text-swamp-300">Midt- og Nordjylland</span>
            </h1>
            <p className="text-lg sm:text-xl text-stone-200 leading-relaxed mb-10 font-medium">
              Professionel skovservice og naturpleje i Nordjylland og resten af landet. Som erfaren skoventreprenør sikrer vi høj effektivitet, råstyrke og driftssikkerhed med vores topmoderne maskinpark.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
              <Link 
                to="/kontakt" 
                className="w-full sm:w-auto bg-swamp-900 hover:bg-swamp-800 text-white px-8 py-4 rounded-xl font-bold tracking-wide transition-all shadow-xl shadow-black/40 border border-swamp-700 hover:border-swamp-500 hover:scale-[1.02] flex items-center justify-center gap-2 text-base cursor-pointer"
              >
                Få et uforpligtende tilbud
                <ArrowRight className="w-5 h-5 text-swamp-300 stroke-[2.5]" />
              </Link>
              <a 
                href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} 
                className="w-full sm:w-auto bg-swamp-950 hover:bg-swamp-900 border-2 border-swamp-700 hover:border-swamp-500 text-white px-8 py-4 rounded-xl font-bold tracking-wide transition-all shadow-lg flex items-center justify-center gap-2 text-base cursor-pointer"
              >
                <Phone className="w-5 h-5 text-swamp-300" />
                Ring {COMPANY_INFO.phone}
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-stone-900/95 border-t border-stone-800 backdrop-blur-md py-6 hidden lg:block">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-4 gap-8">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center border-r border-stone-800/80 last:border-0">
                  <span className="block text-3xl sm:text-4xl font-black text-swamp-300 uppercase tracking-tight">
                    {stat.value}
                  </span>
                  <span className="block text-xs text-stone-300 uppercase tracking-wider font-bold mt-1">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      {pageData && (
        <section className="py-20 bg-stone-900 border-t border-stone-800">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="prose prose-invert prose-emerald mx-auto max-w-none">
              <div 
                dangerouslySetInnerHTML={{ __html: pageData.content.da || pageData.content }} 
                className="text-stone-300 [&>h1]:text-3xl [&>h1]:md:text-4xl [&>h1]:font-black [&>h1]:text-white [&>h1]:uppercase [&>h1]:tracking-tight [&>h1]:mb-6 [&>h2]:text-3xl [&>h2]:md:text-4xl [&>h2]:font-black [&>h2]:text-white [&>h2]:uppercase [&>h2]:tracking-tight [&>h2]:mb-6 [&>p]:text-lg [&>p]:leading-relaxed [&>p]:mb-6 [&>p]:text-stone-400 [&_a]:text-emerald-400 [&_a]:underline [&_a:hover]:text-emerald-300 [&_a]:transition-colors [&_a]:font-medium"
              />
            </div>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} 
                className="w-full sm:w-auto bg-swamp-900 hover:bg-swamp-800 text-white px-8 py-4 rounded-xl font-bold tracking-wide transition-all shadow-xl shadow-black/40 border border-swamp-700 hover:border-swamp-500 hover:scale-[1.02] flex items-center justify-center gap-2 text-base cursor-pointer"
              >
                <Phone className="w-5 h-5 text-swamp-300" />
                Ring til os på {COMPANY_INFO.phone}
              </a>
              <Link 
                to="/kontakt" 
                className="w-full sm:w-auto bg-swamp-950 hover:bg-swamp-900 border-2 border-swamp-700 hover:border-swamp-500 text-white px-8 py-4 rounded-xl font-bold tracking-wide transition-all flex items-center justify-center gap-2 text-base"
              >
                Få et uforpligtende tilbud
                <ArrowRight className="w-4 h-4 text-swamp-300" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Main Categories Section */}
      <section className="py-24 bg-stone-800 border-t border-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-500">
              Vores Kerneområder
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-white uppercase tracking-tight mt-2">
              Skoventreprenør med flere kerneområder
            </h2>
            <p className="text-stone-400 mt-4 text-base">
              Som skoventreprenør løser vi opgaver for private skovejere, kommuner, erhverv og Naturstyrelsen i hele Danmark med fokus på sikkerhed, erfaring og kvalitet.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {categories.map((cat, idx) => {
              const Icon = cat.icon;
              return (
                <div 
                  key={idx} 
                  className="bg-stone-950 border border-stone-800/80 rounded-2xl overflow-hidden flex flex-col h-full"
                >
                  {/* Image */}
                  <div className="h-56 relative overflow-hidden bg-stone-900">
                    <img 
                      src={cat.image} 
                      alt={cat.title} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-950 to-transparent" />
                    <div className="absolute bottom-4 left-4 bg-swamp-900 border border-swamp-700 text-swamp-300 p-2.5 rounded-xl shadow-lg font-bold">
                      <Icon className="w-6 h-6 stroke-[2.5]" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8 flex-grow flex flex-col justify-between">
                    <div>
                      <p className="text-xl font-bold text-white uppercase tracking-tight mb-3">
                        {cat.title}
                      </p>
                      <p className="text-stone-300 text-sm leading-relaxed mb-6">
                        {cat.description}
                      </p>
                      <div className="space-y-2.5 mb-8">
                        {cat.subservices.map((sub, sIdx) => (
                          <div key={sIdx} className="flex items-center gap-2 text-stone-200 text-sm font-medium">
                            <CheckCircle className="w-4 h-4 text-swamp-300 shrink-0" />
                            {sub.href ? (
                              <Link 
                                to={sub.href} 
                                className="hover:text-swamp-200 transition-colors underline decoration-stone-700 hover:decoration-swamp-400/60"
                              >
                                {sub.name}
                              </Link>
                            ) : (
                              <span>{sub.name}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <Link 
                      to={cat.href} 
                      className="w-full py-3 px-4 rounded-xl bg-swamp-900 hover:bg-swamp-800 border border-swamp-700 hover:border-swamp-500 text-white font-bold text-sm tracking-wide transition-all flex items-center justify-center gap-2 group cursor-pointer"
                    >
                      <span>Læs mere</span>
                      <ArrowRight className="w-4 h-4 text-swamp-300 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Section CTA */}
          <div className="mt-16 text-center bg-stone-950 border border-stone-800/80 rounded-2xl p-8 max-w-3xl mx-auto shadow-xl">
            <h4 className="text-lg font-bold text-white uppercase tracking-tight mb-2">
              Har du brug for rådgivning eller et tilbud?
            </h4>
            <p className="text-stone-300 text-sm mb-6">
              Vi står klar til at hjælpe dig med dit næste projekt. Ring direkte til os for en uforpligtende snak.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} 
                className="w-full sm:w-auto bg-swamp-900 hover:bg-swamp-800 text-white px-6 py-3.5 rounded-xl font-bold tracking-wide transition-all shadow-lg shadow-black/40 border border-swamp-700 hover:border-swamp-500 hover:scale-[1.02] flex items-center justify-center gap-2 text-sm cursor-pointer"
              >
                <Phone className="w-4 h-4 text-swamp-300" />
                Ring {COMPANY_INFO.phone}
              </a>
              <Link 
                to="/kontakt" 
                className="w-full sm:w-auto bg-swamp-950 hover:bg-swamp-900 border-2 border-swamp-700 hover:border-swamp-500 text-white px-6 py-3.5 rounded-xl font-bold tracking-wide transition-all flex items-center justify-center gap-2 text-sm"
              >
                Kontakt os her
                <ArrowRight className="w-4 h-4 text-swamp-300" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PEFC & Quality Section */}
      <section className="py-24 bg-stone-900 border-t border-stone-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Image Catalog Collage */}
            <div className="relative aspect-4/3 rounded-2xl overflow-hidden border border-stone-800 bg-stone-900">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/lastbil-4_1.jpg" 
                alt="Skoventreprenør maskiner og blokvognstransport" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-stone-900/95 border border-stone-800 backdrop-blur-sm p-6 rounded-xl flex items-center gap-4 shadow-xl">
                <div className="bg-swamp-900 text-swamp-300 p-3 rounded-lg border border-swamp-700 shrink-0 shadow-md">
                  <Award className="w-6 h-6 text-swamp-300 stroke-[2.5]" />
                </div>
                <div>
                  <span className="block text-xs text-swamp-300 font-bold uppercase tracking-wider">Godkendelse</span>
                  <span className="block text-sm font-bold text-white uppercase tracking-tight">Godkendt til PEFC-certificerede skove</span>
                </div>
              </div>
            </div>

            {/* Right: Text Content */}
            <div className="space-y-8">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-500">
                  Hvorfor vælge os?
                </span>
                <h2 className="text-3xl sm:text-4xl font-black text-white uppercase tracking-tight mt-2">
                  Din professionelle skoventreprenør
                </h2>
              </div>

              <p className="text-stone-400 text-base leading-relaxed">
                Hos Vesthimmerlands Maskinskovning ApS vægter vi sikkerhed, kvalitet og miljø højt. Vi råder over en topmoderne maskinpark med bioolie på hydraulikanlæggene og vakuumanlæg på olietankene, hvilket sikrer skånsom drift i naturen.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex gap-3 bg-stone-950/60 border border-stone-800/80 p-3.5 rounded-xl">
                  <div className="bg-swamp-900/90 border border-swamp-700/80 p-2 rounded-lg shrink-0 h-fit">
                    <CheckCircle className="w-5 h-5 text-swamp-300" />
                  </div>
                  <div>
                    <p className="font-black text-white uppercase tracking-tight text-sm">PEFC-Godkendt</p>
                    <p className="text-xs text-stone-300 mt-0.5">Vi overholder alle standarder for bæredygtig skovdrift.</p>
                  </div>
                </div>
                <div className="flex gap-3 bg-stone-950/60 border border-stone-800/80 p-3.5 rounded-xl">
                  <div className="bg-swamp-900/90 border border-swamp-700/80 p-2 rounded-lg shrink-0 h-fit">
                    <CheckCircle className="w-5 h-5 text-swamp-300" />
                  </div>
                  <div>
                    <p className="font-black text-white uppercase tracking-tight text-sm">Bioolie på maskiner</p>
                    <p className="text-xs text-stone-300 mt-0.5">Miljøvenlig drift med bioolie på hydraulikanlæggene.</p>
                  </div>
                </div>
                <div className="flex gap-3 bg-stone-950/60 border border-stone-800/80 p-3.5 rounded-xl">
                  <div className="bg-swamp-900/90 border border-swamp-700/80 p-2 rounded-lg shrink-0 h-fit">
                    <CheckCircle className="w-5 h-5 text-swamp-300" />
                  </div>
                  <div>
                    <p className="font-black text-white uppercase tracking-tight text-sm">Mange års erfaring</p>
                    <p className="text-xs text-stone-300 mt-0.5">Vores dygtige mandskab løser alle typer skovopgaver.</p>
                  </div>
                </div>
                <div className="flex gap-3 bg-stone-950/60 border border-stone-800/80 p-3.5 rounded-xl">
                  <div className="bg-swamp-900/90 border border-swamp-700/80 p-2 rounded-lg shrink-0 h-fit">
                    <CheckCircle className="w-5 h-5 text-swamp-300" />
                  </div>
                  <div>
                    <p className="font-black text-white uppercase tracking-tight text-sm">Hele landet</p>
                    <p className="text-xs text-stone-300 mt-0.5">Vi kører ud til opgaver i hele Danmark fra vores base i Aars.</p>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-stone-900 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a 
                  href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} 
                  className="bg-swamp-900 hover:bg-swamp-800 text-white px-6 py-3.5 rounded-xl font-bold tracking-wide transition-all shadow-lg shadow-black/40 border border-swamp-700 hover:border-swamp-500 hover:scale-[1.02] flex items-center justify-center gap-2 text-sm cursor-pointer"
                >
                  <Phone className="w-4 h-4 text-swamp-300" />
                  Ring {COMPANY_INFO.phone}
                </a>
                <div className="flex items-center gap-4 justify-center sm:justify-start">
                  <Link 
                    to="/om-os" 
                    className="text-swamp-300 hover:text-white font-bold text-sm tracking-wide flex items-center gap-1.5"
                  >
                    Om os
                    <ArrowRight className="w-4 h-4 text-swamp-300" />
                  </Link>
                  <span className="text-stone-800">|</span>
                  <Link 
                    to="/galleri" 
                    className="text-stone-300 hover:text-white font-bold text-sm tracking-wide flex items-center gap-1.5 transition-colors"
                  >
                    Galleri
                    <ArrowRight className="w-4 h-4 text-swamp-300" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <div className="bg-stone-800 px-4 sm:px-6 lg:px-8">
          <GallerySection images={galleryImages} title="Billedgalleri fra vores arbejde" />
          <div className="max-w-7xl mx-auto pb-16 text-center">
            <Link 
              to="/galleri" 
              className="inline-flex items-center gap-2 bg-swamp-900 hover:bg-swamp-800 border-2 border-swamp-700 hover:border-swamp-500 text-white px-8 py-4 rounded-xl font-bold text-base tracking-wide transition-all shadow-xl hover:scale-[1.02] cursor-pointer group"
            >
              <span>Se alle billeder i galleriet</span>
              <ArrowRight className="w-5 h-5 text-swamp-300 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-emerald-900 relative overflow-hidden border-t border-emerald-800">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white rounded-full blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 space-y-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight leading-none">
            Har du brug for en skoventreprenør til din næste opgave?
          </h2>
          <div className="text-emerald-100 text-base sm:text-lg max-w-4xl mx-auto leading-relaxed space-y-4">
            <p>
              Kontakt os, hvis du har brug for en erfaren skoventreprenør til skovservice, naturpleje, maskinskovning, flishugning, stubfræsning, rodfræsning eller vognmandskørsel. Fra vores base i Aars hjælper vi kunder i blandt andet Aalborg, Viborg, Hobro og Randers samt resten af Midt- og Nordjylland.
            </p>
            <p>
              Ring til os på <a href="tel:96982000" className="underline hover:text-white transition-colors">96 98 20 00</a> eller send en mail til <a href="mailto:vms@vhms.dk" className="underline hover:text-white transition-colors">vms@vhms.dk</a>. Vi kommer gerne ud til en besigtigelse og udarbejder et skræddersyet tilbud.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link 
              to="/kontakt" 
              className="w-full sm:w-auto bg-swamp-900 hover:bg-swamp-800 text-white px-8 py-4 rounded-xl font-bold tracking-wide transition-all shadow-2xl shadow-black/40 border border-swamp-700 hover:border-swamp-500 hover:scale-[1.02] flex items-center justify-center gap-2 text-base cursor-pointer"
            >
              <span>Kontakt os her</span>
              <ArrowRight className="w-5 h-5 text-swamp-300 stroke-[2.5]" />
            </Link>
            <a 
              href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} 
              className="w-full sm:w-auto bg-swamp-950 hover:bg-swamp-900 border-2 border-swamp-700 hover:border-swamp-500 text-white px-8 py-4 rounded-xl font-bold tracking-wide transition-all shadow-xl flex items-center justify-center gap-2 text-base cursor-pointer"
            >
              <Phone className="w-5 h-5 text-swamp-300" />
              Ring på {COMPANY_INFO.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
